import { forwardRef, type PropsWithChildren } from 'react';

import { ApiWorkflowDialogContent } from './ApiWorkflowDialogContent';
import { useApiWorkflowDialog } from './useApiWorkflowDialog';
import { DialogButtons } from '../../components/DialogButtons';
import { DialogTitle } from '../../components/DialogTitle/DialogTitle';
import { StyledDialog } from '../../styledComponents/StyledDialog';
import { ApiWorkflowDialogProps } from '../../types';

/**
 * This component manages the dialog component throughout the
 * create api workflow process
 */
export const ApiWorkflowDialog = forwardRef<HTMLElement, PropsWithChildren<ApiWorkflowDialogProps>>(
  (
    {
      id,
      title,
      dialogMessages,
      size,
      displayButtons = true,
      hideDisplayButtonsOnSuccess = false,
      onClose,
      onCloseIconClick,
      onSubmit,
      onlyShowCloseButton = false,
      children,
      isValidForSubmit = true,
      isInitialLoading = false,
      initialLoadingError,
      onRetryInitialLoadingFetch,
      renderConfirmationStep,
      isSubmitting,
      saveButtonColor = 'primary',
      submittingError,
      onRedirectAfterSubmit,
      renderSubmitting,
      renderSubmitted,
      onRetrySubmit,
      successCountdownTime,
      dialogContentStyleProps,
      key,
      NonScrollBottomComponent,
      closeOnBackdropClick = false,
      dividers = false,
      ...rest
    },
    ref
  ) => {
    // handle the dialog state and side effects
    const {
      currentStep,
      isCloseDisabled,
      cancelButton,
      saveButton,
      onClickClose,
      hideActionButtons,
      dialogSizeSx,
      effectiveIsSubmitting,
    } = useApiWorkflowDialog({
      dialogMessages,
      isValidForSubmit,
      isInitialLoading: !!isInitialLoading,
      initialLoadingError,
      isSubmitting: !!isSubmitting,
      submittingError,
      saveButtonColor,
      onClose,
      onSubmit,
      onRedirectAfterSubmit,
      onRetryInitialLoadingFetch,
      onRetrySubmit,
      successCountdownTime,
      shouldShowConfirmationStep: !!renderConfirmationStep,
      hideDisplayButtonsOnSuccess,
      closeOnBackdropClick,
      size,
    });

    return (
      <StyledDialog
        key={key}
        id={id}
        open
        PaperProps={{ sx: dialogSizeSx }}
        fullWidth
        maxWidth="lg"
        onClose={isSubmitting ? undefined : onClickClose}
        {...rest}
      >
        {/* Render title if title is provided */}
        {title && (
          <DialogTitle
            id={`dialog-${id}-title`}
            showClose
            onClose={isSubmitting ? undefined : onCloseIconClick || onClose}
            disabled={isCloseDisabled}
          >
            {title}
          </DialogTitle>
        )}

        {/* Render children */}
        <ApiWorkflowDialogContent
          id={id}
          ref={ref}
          style={dialogContentStyleProps}
          currentStep={currentStep}
          isInitialLoading={isInitialLoading}
          initialLoadingError={initialLoadingError}
          submittingError={submittingError}
          isSubmitting={effectiveIsSubmitting}
          dialogMessages={dialogMessages}
          renderSubmitting={renderSubmitting}
          renderSubmitted={renderSubmitted}
          renderConfirmationStep={renderConfirmationStep}
          successCountdownTime={successCountdownTime}
          hideDisplayButtonsOnSuccess={hideDisplayButtonsOnSuccess}
          onClose={onClose}
          onRedirectAfterSubmit={onRedirectAfterSubmit}
          dividers={dividers}
        >
          {children}
        </ApiWorkflowDialogContent>
        {NonScrollBottomComponent}
        {/* Render actions */}
        {displayButtons && !hideActionButtons && (
          <DialogButtons
            id="dialog-actions"
            open
            cancelButton={cancelButton}
            saveButton={onlyShowCloseButton ? undefined : saveButton}
          />
        )}
      </StyledDialog>
    );
  }
);
